/**
 * Outlets by Time — PDF export.
 *
 * Landscape A4, NYMR green, jsPDF + autotable, downloaded straight to the
 * browser (no popup / print dialog — same pattern as the P&L and Master View
 * exports).
 *
 * Contents:
 *   1. Cover summary — every outlet, sales / COGS / fixed / profit / margin,
 *      plus the cost model in force and which cost-of-goods source was used.
 *   2. Per outlet — sales and profit by month, then profit by hour of day.
 *   3. Loss register — if a month is selected on screen, the full list of
 *      loss-making days and hours (the screen caps the hour list at 400 rows;
 *      here it is complete, which is the point of the export).
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ApiDayRow,
  Bucket,
  CogsSource,
  FISCAL_MONTHS,
  OptimiseResult,
  OutletModel,
  buildDayModels,
  fiscalMonthLabel,
  foldSlots,
  foldedSlotLabel,
  marginPct,
} from "./execOutletsModel";

const NYMR_GREEN: [number, number, number] = [11, 61, 43];
const LOSS_RED: [number, number, number] = [179, 38, 30];

const GBP0 = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

function money(n: number): string {
  const v = Math.round(n);
  return v < 0 ? `-${GBP0.format(Math.abs(v))}` : GBP0.format(v);
}

function pctText(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "--";
  return `${n.toFixed(1)}%`;
}

export interface ExecPdfInput {
  fiscalYearLabel: string;
  outlets: OutletModel[];
  seasonTotals: Bucket;
  cogsSource: CogsSource;
  allHours: boolean;
  lossMonth: number | null;
  dayRows: Record<number, ApiDayRow[]>;
  costAdjustments: { month: number; location: string; cost_adjust: number }[];
}

export function exportExecPdf(input: ExecPdfInput): void {
  const {
    fiscalYearLabel, outlets, seasonTotals, cogsSource, allHours,
    lossMonth, dayRows, costAdjustments,
  } = input;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });

  const header = (title: string, sub?: string) => {
    doc.setFillColor(...NYMR_GREEN);
    doc.rect(0, 0, pageW, 18, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.text(title, 12, 8.5);
    doc.setFontSize(8);
    doc.text(sub ?? `Season ${fiscalYearLabel}`, 12, 14);
    doc.text(`Printed ${printedOn}`, pageW - 12, 14, { align: "right" });
    doc.setTextColor(0, 0, 0);
  };

  // -------------------------------------------------------------------------
  // 1. Cover summary
  // -------------------------------------------------------------------------
  header("Outlets by Time", `Season ${fiscalYearLabel} — outlet profitability by hour of day`);

  doc.setFontSize(8);
  doc.text(
    `Cost of goods: ${cogsSource === "actual" ? "actual MERAC cost, modelled % on uncosted lines" : "modelled %"}` +
      `   |   Hours: ${allHours ? "all clock hours" : "09:30-17:30, with early/late edge buckets"}` +
      `   |   Staffing, utilities and maintenance are fixed per trading day.`,
    12,
    24
  );

  autoTable(doc, {
    startY: 28,
    head: [["Outlet", "Category", "Sales (net)", "Cost of goods", "Fixed costs", "Profit", "Margin", "Trading days", "Fixed/day"]],
    body: [...outlets]
      .sort((a, b) => b.profit - a.profit)
      .map((o) => [
        o.location,
        o.category,
        money(o.net),
        money(o.cogs),
        money(o.fixed),
        money(o.profit),
        pctText(marginPct(o)),
        String(o.tradingDays),
        money(o.fixedPerDay),
      ]),
    foot: [[
      "All outlets", "",
      money(seasonTotals.net), money(seasonTotals.cogs), money(seasonTotals.fixed),
      money(seasonTotals.profit), pctText(marginPct(seasonTotals)), "", "",
    ]],
    styles: { fontSize: 8, cellPadding: 1.6 },
    headStyles: { fillColor: NYMR_GREEN, fontSize: 8 },
    footStyles: { fillColor: [235, 240, 236], textColor: 20, fontStyle: "bold" },
    columnStyles: {
      2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" },
      5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" },
    },
    // colour any loss-making outlet's profit cell
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 5 && d.cell.raw && String(d.cell.raw).startsWith("-")) {
        d.cell.styles.textColor = LOSS_RED;
      }
    },
  });

  // cost model in force
  const modelY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  autoTable(doc, {
    startY: modelY,
    head: [["Cost model in force", "Cost of Goods", "Staffing", "Utilities", "Maintenance", "Profit margin"]],
    body: outlets.map((o) => [
      `${o.location}${o.costs.isOverride ? " (override)" : ""}`,
      `${o.costs.cogs}%`, `${o.costs.staffing}%`, `${o.costs.utilities}%`,
      `${o.costs.maintenance}%`, `${o.costs.margin.toFixed(1)}%`,
    ]),
    styles: { fontSize: 7.5, cellPadding: 1.3 },
    headStyles: { fillColor: NYMR_GREEN, fontSize: 7.5 },
    columnStyles: {
      1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" },
      4: { halign: "right" }, 5: { halign: "right" },
    },
  });

  // -------------------------------------------------------------------------
  // 2. Per outlet
  // -------------------------------------------------------------------------
  for (const o of outlets) {
    doc.addPage();
    header(o.location, `${o.category} — season ${fiscalYearLabel}`);

    const months = o.months;
    autoTable(doc, {
      startY: 24,
      head: [["", ...months.map((m) => FISCAL_MONTHS[m.month]), "Total"]],
      body: [
        ["Sales (net)", ...months.map((m) => money(m.net)), money(o.net)],
        ["Cost of goods", ...months.map((m) => money(m.cogs)), money(o.cogs)],
        ["Fixed costs", ...months.map((m) => money(m.fixed)), money(o.fixed)],
        ["Profit", ...months.map((m) => money(m.profit)), money(o.profit)],
        ["Margin", ...months.map((m) => pctText(marginPct(m))), pctText(marginPct(o))],
        ["Trading days", ...months.map((m) => String(m.tradingDays)), String(o.tradingDays)],
      ],
      styles: { fontSize: 7.5, cellPadding: 1.3, halign: "right" },
      headStyles: { fillColor: NYMR_GREEN, fontSize: 7.5, halign: "right" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 26 } },
      didParseCell: (d) => {
        if (d.section === "body" && (d.row.index === 3 || d.row.index === 4)) {
          if (String(d.cell.raw).startsWith("-")) d.cell.styles.textColor = LOSS_RED;
        }
      },
    });

    // profit by hour of day, whole season for this outlet
    const slots = foldSlots(o.slots, allHours);
    autoTable(doc, {
      startY: (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6,
      head: [["Hour of day", ...slots.map((s) => foldedSlotLabel(s.slot))]],
      body: [
        ["Sales (net)", ...slots.map((s) => money(s.net))],
        ["Profit", ...slots.map((s) => money(s.profit))],
        ["Margin", ...slots.map((s) => pctText(marginPct(s)))],
      ],
      styles: { fontSize: 6.8, cellPadding: 1.1, halign: "right" },
      headStyles: { fillColor: NYMR_GREEN, fontSize: 6.8, halign: "right" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 24 } },
      didParseCell: (d) => {
        if (d.section === "body" && d.row.index >= 1 && String(d.cell.raw).startsWith("-")) {
          d.cell.styles.textColor = LOSS_RED;
        }
      },
    });
  }

  // -------------------------------------------------------------------------
  // 3. Loss register (only when a month is selected on screen)
  // -------------------------------------------------------------------------
  const rows = lossMonth != null ? dayRows[lossMonth] : undefined;
  if (lossMonth != null && rows) {
    const lossDays: string[][] = [];
    const lossHours: string[][] = [];

    for (const o of outlets) {
      const m = o.months.find((x) => x.month === lossMonth);
      if (!m) continue;
      const adj =
        costAdjustments.find((a) => a.location === o.location && a.month === lossMonth)?.cost_adjust ?? 0;
      const days = buildDayModels(rows, o, cogsSource, adj, m.net);
      for (const d of days) {
        const label = new Date(`${d.date}T00:00:00Z`).toLocaleDateString("en-GB", {
          weekday: "short", day: "numeric", month: "short", timeZone: "UTC",
        });
        if (d.profit < 0) {
          lossDays.push([o.location, label, money(d.net), money(d.profit)]);
        }
        for (const s of d.slots) {
          if (s.profit < 0) {
            lossHours.push([o.location, label, foldedSlotLabel(s.slot), money(s.net), money(s.profit)]);
          }
        }
      }
    }

    doc.addPage();
    header(
      "Loss-making days & hours",
      `${FISCAL_MONTHS[lossMonth]} — season ${fiscalYearLabel} (complete list)`
    );

    autoTable(doc, {
      startY: 24,
      head: [["Outlet", "Date", "Sales (net)", "Profit"]],
      body: lossDays.length ? lossDays : [["--", "Every day made money", "", ""]],
      styles: { fontSize: 7.5, cellPadding: 1.3 },
      headStyles: { fillColor: NYMR_GREEN, fontSize: 7.5 },
      columnStyles: { 2: { halign: "right" }, 3: { halign: "right", textColor: LOSS_RED } },
    });

    doc.addPage();
    header("Loss-making hours", `${FISCAL_MONTHS[lossMonth]} — season ${fiscalYearLabel}`);
    autoTable(doc, {
      startY: 24,
      head: [["Outlet", "Date", "Hour", "Sales (net)", "Profit"]],
      body: lossHours.length ? lossHours : [["--", "Every trading hour made money", "", "", ""]],
      styles: { fontSize: 7, cellPadding: 1.1 },
      headStyles: { fillColor: NYMR_GREEN, fontSize: 7 },
      columnStyles: { 3: { halign: "right" }, 4: { halign: "right", textColor: LOSS_RED } },
    });
  }

  // page numbers
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text(`${i} / ${total}`, pageW - 12, doc.internal.pageSize.getHeight() - 6, { align: "right" });
  }

  doc.save(`NYMR-outlets-by-time-${fiscalYearLabel.replace("/", "-")}.pdf`);
}

// ---------------------------------------------------------------------------
//  Optimiser PDF — the whole "Optimise opening times" panel on one sheet.
// ---------------------------------------------------------------------------
export interface OptimiserPdfInput {
  outlet: OutletModel;
  fiscalYearLabel: string;
  maxStaff: number;
  minPerDay: number;
  minPerMonth: number;
  result: OptimiseResult;
  baseline: { current: number; lastYear: number | null } | null;
}

export function exportOptimiserPdf(input: OptimiserPdfInput): void {
  const { outlet, fiscalYearLabel, maxStaff, minPerDay, minPerMonth, result, baseline } = input;
  const fy = 2000 + Number(fiscalYearLabel.slice(-2)); // for month labels

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });

  doc.setFillColor(...NYMR_GREEN);
  doc.rect(0, 0, pageW, 18, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.text(`Optimise opening times — ${outlet.location}`, 12, 8.5);
  doc.setFontSize(8);
  doc.text(`${outlet.category} · season ${fiscalYearLabel}`, 12, 14);
  doc.text(`Printed ${printedOn}`, pageW - 12, 14, { align: "right" });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(9);
  doc.text(
    `Max paid staff: ${maxStaff}   |   Min profit/day: ${money(minPerDay)}   |   Min profit/month: ${money(minPerMonth)}` +
      `   |   Staff rate: ${money(result.ratePerHour)}/hr`,
    12, 26
  );

  const upliftCurrent = baseline ? result.year.profit - baseline.current : null;
  const upliftLast = baseline && baseline.lastYear != null ? result.year.profit - baseline.lastYear : null;
  const signed = (n: number | null) => (n == null ? "n/a" : (n >= 0 ? "+" : "") + money(n));

  autoTable(doc, {
    startY: 30,
    head: [["Optimised profit", "Current (actual YTD)", "Uplift vs current", "Last year (same months)", "Uplift vs last year", "Closed"]],
    body: [[
      money(result.year.profit),
      baseline ? money(baseline.current) : "--",
      signed(upliftCurrent),
      baseline?.lastYear != null ? money(baseline.lastYear) : "n/a",
      signed(upliftLast),
      `${result.monthsClosed} months · ${result.daysClosed} days`,
    ]],
    styles: { fontSize: 8, halign: "center" },
    headStyles: { fillColor: NYMR_GREEN, fontSize: 8, halign: "center" },
  });

  autoTable(doc, {
    startY: (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6,
    head: [["Month", "Status", "Sales", "COGS", "Staffing", "Fixed", "Profit", "Margin"]],
    body: result.months.map((m) => [
      fiscalMonthLabel(m.month, fy),
      m.open ? `open · ${m.daysOpen}d${m.daysClosed ? ` (${m.daysClosed} shut)` : ""}` : "closed",
      money(m.sales), money(m.cogs), money(m.staffing), money(m.fixed), money(m.profit), pctText(m.marginPct),
    ]),
    foot: [[
      "Year", "",
      money(result.year.sales), money(result.year.cogs), money(result.year.staffing),
      money(result.year.fixed), money(result.year.profit), pctText(result.year.marginPct),
    ]],
    styles: { fontSize: 8, cellPadding: 1.4 },
    headStyles: { fillColor: NYMR_GREEN, fontSize: 8 },
    footStyles: { fillColor: [235, 240, 236], textColor: 20, fontStyle: "bold" },
    columnStyles: {
      2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" },
      5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" },
    },
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 6 && String(d.cell.raw).startsWith("-")) {
        d.cell.styles.textColor = LOSS_RED;
      }
      if (d.section === "body" && d.column.index === 1 && d.cell.raw === "closed") {
        d.cell.styles.textColor = LOSS_RED;
      }
    },
  });

  // Opening calendar: open/closed per month + the closed days in open months.
  const fmtDay = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  autoTable(doc, {
    startY: (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6,
    head: [["Month", "Opening", "Closed days"]],
    body: result.months.map((m) => [
      fiscalMonthLabel(m.month, fy),
      m.open ? `Open — ${m.daysOpen} day(s)${m.daysClosed ? `, ${m.daysClosed} closed` : ""}` : "Closed all month",
      m.open
        ? (m.closedDates.length ? m.closedDates.map(fmtDay).join(", ") : "—")
        : `all ${m.daysClosed} trading day(s)`,
    ]),
    styles: { fontSize: 7.5, cellPadding: 1.3, valign: "top" },
    headStyles: { fillColor: NYMR_GREEN, fontSize: 7.5 },
    columnStyles: { 0: { cellWidth: 26 }, 1: { cellWidth: 48 } },
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 1 && String(d.cell.raw).startsWith("Closed")) {
        d.cell.styles.textColor = LOSS_RED;
      }
    },
  });

  doc.setFontSize(7.5);
  doc.setTextColor(90);
  const y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  doc.text(
    "Sales, COGS and staffing are the OPEN days only; fixed overhead is the full month, incurred whether open or shut. " +
      "Opening is a marginal call — closing saves staffing and cost of goods, not the committed overhead. " +
      "A projection on historic trade, not a forecast; closing an hour assumes its takings are lost (worst case).",
    12, y, { maxWidth: pageW - 24 }
  );

  doc.save(`NYMR-optimise-${outlet.location.replace(/\W+/g, "-")}-${fiscalYearLabel.replace("/", "-")}.pdf`);
}
